import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfDay,
  endOfWeek,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
  subYears
} from 'date-fns';

const MIN_START_DATE = startOfDay(subYears(new Date(), 150));
const MAX_END_DATE = endOfDay(addYears(new Date(), 150));

const clampToMinDate = (date) => {
  if (!date) return date;
  return isBefore(date, MIN_START_DATE) ? MIN_START_DATE : date;
};

const clampToMaxDate = (date) => {
  if (!date) return date;
  return isAfter(date, MAX_END_DATE) ? MAX_END_DATE : date;
};

const clampToBounds = (date) => clampToMaxDate(clampToMinDate(date));

const shiftDate = (date, unit, amount, forward) => {
  if (unit === 'days') return forward ? addDays(date, amount) : subDays(date, amount);
  if (unit === 'weeks') return forward ? addWeeks(date, amount) : subWeeks(date, amount);
  if (unit === 'months') return forward ? addMonths(date, amount) : subMonths(date, amount);
  return forward ? addDays(date, amount) : subDays(date, amount);
};

// fromZonedTime so the ISO instant is the zone's day boundary, not the browser's
const boundaryToIso = (date, timeZone) =>
  (timeZone ? fromZonedTime(date, timeZone) : date).toISOString();

export function getDateRange(unit, amount, timeZone, direction = 'past') {
  const isFuture = direction === 'future';
  const safeAmount = Math.max(0, Number(amount) || 0);
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();
  const shifted = shiftDate(now, unit, safeAmount, isFuture);

  if (isFuture) {
    return {
      startDate: boundaryToIso(startOfDay(now), timeZone),
      endDate: boundaryToIso(clampToMaxDate(endOfDay(shifted)), timeZone)
    };
  }

  return {
    startDate: boundaryToIso(startOfDay(clampToBounds(shifted)), timeZone),
    endDate: boundaryToIso(clampToMaxDate(endOfDay(now)), timeZone)
  };
}

export function getDateRangeByUnit(unit, amount, timeZone, direction = 'past') {
  const isFuture = direction === 'future';
  const safeAmount = Math.max(0, Number(amount) || 0);
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();
  const shifted = clampToBounds(shiftDate(now, unit, safeAmount, isFuture));

  if (unit === 'weeks') {
    const weekStart = startOfWeek(shifted);
    return {
      startDate: boundaryToIso(clampToMinDate(weekStart), timeZone),
      endDate: boundaryToIso(clampToMaxDate(endOfDay(endOfWeek(shifted))), timeZone)
    };
  }

  return {
    startDate: boundaryToIso(startOfDay(shifted), timeZone),
    endDate: boundaryToIso(clampToMaxDate(endOfDay(shifted)), timeZone)
  };
}

const getMonthToDateRange = (timeZone) => {
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();
  return {
    startDate: boundaryToIso(startOfMonth(now), timeZone),
    endDate: boundaryToIso(clampToMaxDate(endOfDay(now)), timeZone)
  };
};

const PAST_OPTIONS = [
  { id: 'yesterday', label: 'Yesterday', unit: 'days', amount: 1 },
  { id: 'last-week', label: 'Last week', unit: 'weeks', amount: 1 },
  { id: 'month-to-date', label: 'Month to Date', build: getMonthToDateRange },
  { id: 'last-30-days', label: 'Last 30 days', unit: 'days', amount: 30, range: true },
  { id: 'last-60-days', label: 'Last 60 days', unit: 'days', amount: 60, range: true },
  { id: 'last-90-days', label: 'Last 90 days', unit: 'days', amount: 90, range: true },
  { id: 'last-6-months', label: 'Last 6 months', unit: 'months', amount: 6, range: true }
];

const FUTURE_OPTIONS = [
  { id: 'tomorrow', label: 'Tomorrow', unit: 'days', amount: 1, direction: 'future' },
  { id: 'next-week', label: 'Next week', unit: 'weeks', amount: 1, direction: 'future' },
  {
    id: 'next-30-days',
    label: 'Next 30 days',
    unit: 'days',
    amount: 30,
    range: true,
    direction: 'future'
  },
  {
    id: 'next-60-days',
    label: 'Next 60 days',
    unit: 'days',
    amount: 60,
    range: true,
    direction: 'future'
  },
  {
    id: 'next-90-days',
    label: 'Next 90 days',
    unit: 'days',
    amount: 90,
    range: true,
    direction: 'future'
  }
];

const buildOption = (opt, timeZone) => ({
  id: opt.id,
  label: opt.label,
  value: opt.build
    ? opt.build(timeZone)
    : opt.range
      ? getDateRange(opt.unit, opt.amount, timeZone, opt.direction)
      : getDateRangeByUnit(opt.unit, opt.amount, timeZone, opt.direction)
});

const withBookends = (options, timeZone) => [
  { id: 'all-time', label: 'All time', value: null },
  { id: 'today', label: 'Today', value: getDateRangeByUnit('days', 0, timeZone) },
  ...options,
  { id: 'custom-date-range', label: 'Custom date range', value: null }
];

export const getDateOptions = (timeZone) =>
  withBookends(
    PAST_OPTIONS.map((o) => buildOption(o, timeZone)),
    timeZone
  );

export const getFutureDateOptions = (timeZone) =>
  withBookends(
    FUTURE_OPTIONS.map((o) => buildOption(o, timeZone)),
    timeZone
  );

export const getAllDateOptions = (timeZone) =>
  withBookends(
    [...PAST_OPTIONS, ...FUTURE_OPTIONS].map((o) => buildOption(o, timeZone)),
    timeZone
  );

export const getOptionsByDirection = (direction, timeZone) => {
  if (direction === 'all') return getAllDateOptions(timeZone);
  if (direction === 'future') return getFutureDateOptions(timeZone);
  return getDateOptions(timeZone);
};

const buildSuggestion = (number, label1, labelN, unit, timezone, direction) => ({
  label: number === 1 ? label1 : `${labelN} ${number} ${unit}`,
  value: getDateRange(unit.toLowerCase(), number, timezone, direction)
});

const pastSuggestions = (number, timezone) => [
  buildSuggestion(number, 'Yesterday', 'Last', 'Days', timezone),
  buildSuggestion(number, 'Last Week', 'Last', 'Weeks', timezone),
  buildSuggestion(number, 'Last Month', 'Last', 'Months', timezone)
];

const futureSuggestions = (number, timezone) => [
  buildSuggestion(number, 'Tomorrow', 'Next', 'Days', timezone, 'future'),
  buildSuggestion(number, 'Next Week', 'Next', 'Weeks', timezone, 'future'),
  buildSuggestion(number, 'Next Month', 'Next', 'Months', timezone, 'future')
];

export const getSuggestions = (direction, number, timezone) => {
  if (direction === 'all')
    return [...pastSuggestions(number, timezone), ...futureSuggestions(number, timezone)];
  if (direction === 'future') return futureSuggestions(number, timezone);
  return pastSuggestions(number, timezone);
};
